import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { GA_PDP_DIAGRAM } from 'constants/analyticsConstants';
import { LineItem, PartDetailsAndSchematicResponse } from 'types/parts-diagram';
import PartsOverviewCardWrapper from './parts-overview-card-wrapper';
import cx from 'classnames';
import Styles from './parts-overview.module.scss';
import { CatBadge, CatButton } from 'components/blocks-components';
import { useTranslations } from 'next-intl';
import { Conditional, useBreakpoint } from '@cat-ecom/pcc-components';
import { PAGE_BREAKPOINTS } from 'constants/common';
import { useQuickToast } from 'hooks/use-toast';
import { useViewItemListEvent } from 'hooks/analytics/use-view-item-list-event';
import { CartResponse } from 'services/cart/types';
import { usePartDiagram } from 'hooks/use-part-diagram/parts-diagram-context';
import PartsCheckPrice from 'components/parts-check-price/parts-check-price';
import {
  countValidParts,
  reOrderPartItems
} from 'components/parts-diagram/utils';
import { useCoreContext } from 'providers/core-provider';
import { useEsiteCtx } from 'services/esite/context';
import { useSelectItemEvent } from 'hooks/analytics/use-select-item-event';
import { getFormattedItemData } from 'components/product-comparison-grid/product-comparison-card/utils';
import { useAddToCartItemEvent } from 'hooks/analytics/use-add-to-cart-item';

export interface PartsOverviewProps {
  partDetailsAndSchematicsData: PartDetailsAndSchematicResponse;
  handlePartView: (lineItem: LineItem) => void;
  onHover?: (referenceNumber: string | null) => void;
  selectedItem?: string | null;
  shouldHideBtn?: boolean;
  partsDiagramMobileView?: boolean;
  isPccPdpHeaderUpdateMobile?: boolean;
  fitmentList?: ProductFitmentResponse;
  fromProductPage?: boolean;
  contactDealerBtn?: React.ReactNode;
  cartDetails?: CartResponse;
  contactDealerAllProductsBtn?: React.ReactNode;
}

const PartsOverview: FC<PartsOverviewProps> = ({
  partDetailsAndSchematicsData,
  cartDetails,
  handlePartView,
  onHover,
  selectedItem,
  shouldHideBtn,
  partsDiagramMobileView = false,
  isPccPdpHeaderUpdateMobile = false,
  fitmentList,
  fromProductPage = false,
  contactDealerBtn,
  contactDealerAllProductsBtn
}) => {
  const pdpGaViewItemFired = useRef(false);
  const { handleAddToCart } = useCoreContext();
  const { storeId, locale } = useEsiteCtx();
  const { currency = '', lineItems = [] } = partDetailsAndSchematicsData || {};
  const { isCatCorp } = usePartDiagram();
  const toast = useQuickToast();
  const { fireEvent: fireViewItemListEvent } = useViewItemListEvent();
  const [gaEventAlreadyFired, setGaEventAlreadyFired] = useState(false);

  const t = useTranslations();
  const [lineItemsUpdated, setLineItemsUpdated] = useState(lineItems);
  const [isExpanded, setIsExpanded] = useState(false);
  const isMobile = !useBreakpoint(PAGE_BREAKPOINTS.MD);
  const { fireEvent: fireSelectItemEvent } = useSelectItemEvent();
  const { fireEvent: addToCartItemEvent } = useAddToCartItemEvent();

  useEffect(() => {
    if (
      !pdpGaViewItemFired.current &&
      isMobile &&
      lineItems.length > 0 &&
      partsDiagramMobileView
    ) {
      const gaProducts = lineItems.map((item: LineItem) => {
        const product: Product = {
          ...item,
          imageURL: '',
          type: ''
        };
        return product;
      });
      const firstItem = gaProducts.slice(0, 1);
      const diagramViewItemList = {
        item_list_id: GA_PDP_DIAGRAM,
        item_list_name: GA_PDP_DIAGRAM
      };
      const diagramCartPayload = {
        products: firstItem,
        itemListData: diagramViewItemList,
        cartItemsData: cartDetails,
        cartId: cartDetails?.orderHeader?.orderId,
        isProductsData: true,
        isSearchPageEvent: true
      };

      fireViewItemListEvent(diagramCartPayload);
      pdpGaViewItemFired.current = true;
    }
  }, [
    fireViewItemListEvent,
    lineItems,
    cartDetails,
    isMobile,
    partsDiagramMobileView
  ]);

  const products = lineItems.map((item: LineItem) => {
    const product: Product = {
      ...item,
      imageURL: '',
      type: ''
    };
    return product;
  });

  useEffect(() => {
    const diagramViewItemList = {
      item_list_id: GA_PDP_DIAGRAM,
      item_list_name: GA_PDP_DIAGRAM
    };
    const diagramCartPayload = {
      products,
      itemListData: diagramViewItemList,
      cartItemsData: cartDetails,
      cartId: cartDetails?.orderHeader?.orderId,
      isProductsData: true,
      isSearchPageEvent: true
    };

    if (lineItems?.length > 0 && !gaEventAlreadyFired) {
      fireViewItemListEvent(diagramCartPayload);
      setGaEventAlreadyFired(true);
    }
  }, [
    cartDetails,
    products,
    lineItems,
    fireViewItemListEvent,
    gaEventAlreadyFired
  ]);

  useEffect(() => {
    if (lineItems?.length && isMobile) {
      const result =
        selectedItem !== null
          ? reOrderPartItems(lineItems, selectedItem)
          : lineItems;

      const updatedLineItems =
        !isExpanded && partsDiagramMobileView
          ? result?.slice(0, 1) || []
          : [...(result || [])];

      setLineItemsUpdated(updatedLineItems);
    } else {
      setLineItemsUpdated(lineItems);
    }
  }, [selectedItem, lineItems, partsDiagramMobileView, isMobile, isExpanded]);

  const onViewMoreClick = () => {
    const result = reOrderPartItems(lineItems, selectedItem);
    const allLineItems = selectedItem ? result?.slice() : lineItems.slice();
    setIsExpanded(true);
    setLineItemsUpdated(allLineItems);

    const gaProducts = allLineItems.map((item: LineItem) => {
      const product: Product = {
        ...item,
        imageURL: '',
        type: ''
      };
      return product;
    });
    const restItems = gaProducts.slice(1);
    const diagramViewItemList = {
      item_list_id: GA_PDP_DIAGRAM,
      item_list_name: GA_PDP_DIAGRAM
    };
    const diagramCartPayload = {
      products: restItems,
      itemListData: diagramViewItemList,
      cartItemsData: cartDetails,
      cartId: cartDetails?.orderHeader?.orderId,
      isProductsData: true,
      isSearchPageEvent: true
    };

    fireViewItemListEvent(diagramCartPayload);
  };
  const onViewLessClick = () => {
    const result = reOrderPartItems(lineItems, selectedItem);
    const lessLineItems = selectedItem
      ? result?.slice(0, 1)
      : lineItems.slice(0, 1);
    setIsExpanded(false);
    setLineItemsUpdated(lessLineItems);
  };
  const checkIfAnyItemHasTotalPrice = useMemo(() => {
    return lineItems.some(
      (item) => item?.priceAndAvailabilityData?.enableAddToCart
    );
  }, [lineItems]);

  const handleAddAllToCart = async () => {
    const filteredLineItems = lineItems?.filter((eachItem) => {
      return eachItem?.priceAndAvailabilityData?.enableAddToCart;
    });
    const partsQtyPriceData = filteredLineItems?.map((item) => {
      return {
        partName: item?.name,
        quantity: item?.quantity,
        partNumber: item?.partNumber
      };
    });
    fireSelectItemEvent({
      itemListId: GA_PDP_DIAGRAM,
      itemListName: GA_PDP_DIAGRAM,
      partName: '',
      index: 0,
      locationId: '',
      isSearchPageEvent: true,
      itemSummary: filteredLineItems,
      item: {
        orderItemId: cartDetails?.orderHeader?.orderId,
        partNumber: '',
        quantity: 1,
        availabilityMessage: '',
        totalPrice: 0
      }
    });
    const priceAndAvailabilityData = filteredLineItems?.map(
      (item) => item?.priceAndAvailabilityData
    );
    addToCartItemEvent({
      locationId: storeId,
      itemSummary: partsQtyPriceData,
      itemData: getFormattedItemData(
        cartDetails?.orderHeader?.orderId,
        priceAndAvailabilityData,
        currency
      ),
      itemListId: GA_PDP_DIAGRAM,
      itemListName: GA_PDP_DIAGRAM
    });
    const { error } = await handleAddToCart(
      {
        items: partsQtyPriceData
      },
      storeId,
      locale
    );
    if (error) {
      toast.error({
        message: t('ITEM_ADD_ERROR')
      });
    } else {
      toast.success({
        message: t('ITEMS_CART_CONFIRMATIONS_PLURAL')
      });
    }
  };
  const allCardsHasContactDealer = useMemo(() => {
    return lineItemsUpdated.every(
      (partItem) =>
        !isCatCorp && !partItem?.priceAndAvailabilityData?.enableAddToCart
    );
  }, [lineItemsUpdated, isCatCorp]);

  return (
    <div
      className={cx(
        'w-100 d-flex flex-column overflow-x-hidden overflow-y-scroll',
        Styles['parts-overview__container'],
        { 'gap-3': partsDiagramMobileView }
      )}
    >
      {lineItemsUpdated.map((partItem, index) => {
        const { partNumber = '' } = partItem || {};
        return (
          <PartsOverviewCardWrapper
            currency={currency}
            partItem={partItem}
            cartId={cartDetails?.orderHeader?.orderId}
            key={partNumber}
            handlePartView={handlePartView}
            onHover={onHover}
            selectedItem={selectedItem}
            index={index}
            shouldHideBtn={shouldHideBtn}
            lastItem={lineItems.length - 1}
            partsDiagramMobileView={partsDiagramMobileView}
            isPccPdpHeaderUpdateMobile={isPccPdpHeaderUpdateMobile}
            isSerialCompatible={fitmentList?.[partNumber]?.isSerialCompatible}
            fromProductPage={fromProductPage}
            contactDealerBtn={contactDealerBtn}
            allCardsHasContactDealer={allCardsHasContactDealer}
          />
        );
      })}
      <Conditional test={partsDiagramMobileView && isMobile}>
        <div className="d-flex justify-content-between gap-2">
          <Conditional
            test={lineItemsUpdated?.length != lineItems?.length}
            fallback={
              <CatButton size="md" onClick={onViewLessClick} fullWidth>
                {t('VIEW_LESS_BUTTON')}
              </CatButton>
            }
          >
            <CatButton size="md" onClick={onViewMoreClick} fullWidth>
              {t('VIEW_ALL')}
              <CatBadge
                className={Styles['part-count-title']}
                text={`${countValidParts(lineItems)}`}
                variant="contrast"
                size="md"
              />
            </CatButton>
          </Conditional>
          <Conditional test={isCatCorp}>
            <PartsCheckPrice
              classNames="mb-0 text-capitalize"
              size="md"
              variant="contrast"
              fullWidth
            />
          </Conditional>
          <Conditional test={!isCatCorp && checkIfAnyItemHasTotalPrice}>
            <CatButton
              size="md"
              className="text-capitalize"
              variant="contrast"
              fullWidth
              data-testid="add-all-to-cart"
              onClick={handleAddAllToCart}
            >
              {t('PM_ADD_ALL_TO_CART')}
            </CatButton>
          </Conditional>
          <Conditional test={allCardsHasContactDealer}>
            {contactDealerAllProductsBtn}
          </Conditional>
        </div>
      </Conditional>
    </div>
  );
};

export default PartsOverview;
